// Density modes (SPEC D20, CONTRACT "Density"). The mode lives on the root
// element as data-density and tokens.css swaps the row and control heights.
// One body size in every mode; text never changes.

export const DENSITY_MODES = ['compact', 'default', 'spacious'] as const;
export type DensityMode = (typeof DENSITY_MODES)[number];

export const DEFAULT_DENSITY: DensityMode = 'default';
export const DENSITY_STORAGE_KEY = 'aleph.density';
export const DENSITY_ATTRIBUTE = 'data-density';

export function isDensityMode(value: unknown): value is DensityMode {
  return typeof value === 'string' && (DENSITY_MODES as readonly string[]).includes(value);
}

/** Row and control heights per mode, px. Mirrors tokens.css; the test keeps them in sync. */
export const DENSITY_ROWS: Readonly<Record<DensityMode, Readonly<Record<string, number>>>> = {
  compact: {
    'row-call': 28,
    'row-group': 24,
    'row-list': 26,
    'row-table': 28,
    'row-action': 22,
    art: 20,
    control: 28,
    'control-sm': 24,
  },
  default: {
    'row-call': 36,
    'row-group': 28,
    'row-list': 30,
    'row-table': 32,
    'row-action': 28,
    art: 24,
    control: 32,
    'control-sm': 28,
  },
  spacious: {
    'row-call': 44,
    'row-group': 32,
    'row-list': 36,
    'row-table': 40,
    'row-action': 32,
    art: 28,
    control: 36,
    'control-sm': 32,
  },
};

/** Minimal storage surface so tests never touch window. */
export interface DensityStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Reads the stored mode; anything unknown or a throwing storage falls back to default. */
export function readStoredDensity(storage: DensityStorage | null): DensityMode {
  if (!storage) {
    return DEFAULT_DENSITY;
  }
  try {
    const raw = storage.getItem(DENSITY_STORAGE_KEY);
    return isDensityMode(raw) ? raw : DEFAULT_DENSITY;
  } catch {
    return DEFAULT_DENSITY;
  }
}

/** Persists the mode. Returns false when storage refused (private mode, quota). */
export function writeStoredDensity(storage: DensityStorage | null, mode: DensityMode): boolean {
  if (!storage) {
    return false;
  }
  try {
    storage.setItem(DENSITY_STORAGE_KEY, mode);
    return true;
  } catch {
    return false;
  }
}

/** Stamps the mode on the root element. Rejects unknown modes loudly. */
export function applyDensity(root: Element, mode: DensityMode): void {
  if (!isDensityMode(mode)) {
    throw new Error(`Unknown density mode: ${String(mode)}`);
  }
  root.setAttribute(DENSITY_ATTRIBUTE, mode);
}

/** Reads the current mode off the root element, default when absent or foreign. */
export function readAppliedDensity(root: Element): DensityMode {
  const raw = root.getAttribute(DENSITY_ATTRIBUTE);
  return isDensityMode(raw) ? raw : DEFAULT_DENSITY;
}

/** Startup: stored mode onto the root. Never throws; storage may be unavailable. */
export function initDensity(root: Element, storage: DensityStorage | null): DensityMode {
  const mode = readStoredDensity(storage);
  applyDensity(root, mode);
  return mode;
}

/** User change: apply, then persist. Returns whether persistence succeeded. */
export function setDensity(root: Element, storage: DensityStorage | null, mode: DensityMode): boolean {
  applyDensity(root, mode);
  return writeStoredDensity(storage, mode);
}
